import type { ReactNode } from 'react';

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <header className="page-header">
      <div>
        <h1 className="page-title">{title}</h1>
        {description ? <p className="page-description">{description}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function Panel({
  title,
  description,
  actions,
  children,
  className = '',
  bodyClassName = '',
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}): JSX.Element {
  return (
    <section className={`panel ${className}`.trim()}>
      {title || actions ? (
        <div className="panel-head">
          <div>
            {title ? <h2 className="panel-title">{title}</h2> : null}
            {description ? <p className="panel-description">{description}</p> : null}
          </div>
          {actions ? <div className="panel-actions">{actions}</div> : null}
        </div>
      ) : null}
      <div className={`panel-body ${bodyClassName}`.trim()}>{children}</div>
    </section>
  );
}

/**
 * A disclosure section. Built on <details> so the open/close affordance, keyboard handling and
 * find-in-page expansion are the browser's rather than ours; `open` is controlled so a caller can
 * expand it in response to something else (the vendor list's "Learn from invoice" does exactly
 * that, opening the card with this section already unfolded).
 */
export function Collapsible({
  title,
  hint,
  open,
  onOpenChange,
  children,
}: {
  title: string;
  hint?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <details
      className="collapsible"
      open={open}
      onToggle={(event) => onOpenChange(event.currentTarget.open)}
    >
      <summary className="collapsible-summary">
        <span className="collapsible-title">{title}</span>
        {hint ? <span className="collapsible-hint">{hint}</span> : null}
      </summary>
      <div className="collapsible-body">{children}</div>
    </details>
  );
}

export function Toolbar({ children }: { children: ReactNode }): JSX.Element {
  return <div className="toolbar">{children}</div>;
}

export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  footer,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info';
  footer?: ReactNode;
}): JSX.Element {
  return (
    <div className={`stat-tile stat-${tone}`}>
      <p className="stat-label">{label}</p>
      <p className="stat-value">{value}</p>
      {hint ? <p className="stat-hint">{hint}</p> : null}
      {footer ? <div className="stat-footer">{footer}</div> : null}
    </div>
  );
}

export function KeyValue({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className={`kv ${wide ? 'kv-wide' : ''}`.trim()}>
      <dt className="kv-label">{label}</dt>
      <dd className="kv-value">{children}</dd>
    </div>
  );
}

export function KeyValueList({
  children,
  columns = 2,
}: {
  children: ReactNode;
  columns?: 1 | 2 | 3;
}): JSX.Element {
  return <dl className={`kv-list kv-cols-${columns}`}>{children}</dl>;
}
